/**
 * Marqueurs du modèle de titre YouTube, composé dans Réglages → Direct YouTube.
 *
 * Miroir de `backend/src/lib/youtube-metadata.ts` (`YOUTUBE_TITLE_FIELDS`,
 * `youtubeTitleTemplateError`) : le serveur reste l'autorité et refuse le même
 * modèle ; l'écran le signale seulement avant l'enregistrement.
 */

export interface YoutubeTitleMarker {
  token: string
  label: string
}

/** Marqueurs proposés à l'insertion. `{programme}`, historique, reste accepté. */
export const YOUTUBE_TITLE_MARKERS: readonly YoutubeTitleMarker[] = [
  { token: '{emoji}',      label: 'Emoji (🔴 direct, 🆕 inédit)' },
  { token: '{titre}',      label: 'Titre' },
  { token: '{sous-titre}', label: 'Sous-titre (avec « — »)' },
]

const LEGACY_MARKER = '{programme}'
const KNOWN = new Set([...YOUTUBE_TITLE_MARKERS.map(marker => marker.token), LEGACY_MARKER])

/** Message d'erreur du modèle, ou `null` s'il est accepté. */
export function youtubeTitleTemplateError(template: string): string | null {
  const unknown = (template.match(/\{[^{}]*\}/g) ?? []).filter(token => !KNOWN.has(token))
  if (unknown.length > 0) {
    return `Marqueur inconnu : ${unknown.join(', ')}. Marqueurs possibles : ${[...KNOWN].join(', ')}`
  }
  if (!template.includes('{titre}') && !template.includes(LEGACY_MARKER)) {
    return 'Le modèle doit contenir {titre}'
  }
  return null
}

/**
 * Insère un marqueur à la position du curseur (ou à la place de la sélection),
 * avec un blanc de part et d'autre quand il touche du texte. Rend le modèle et
 * la position du curseur après le marqueur.
 */
export function insertYoutubeTitleMarker(
  template: string,
  token: string,
  selectionStart: number,
  selectionEnd = selectionStart,
): { value: string; caret: number } {
  const start = Math.max(0, Math.min(selectionStart, template.length))
  const end = Math.max(start, Math.min(selectionEnd, template.length))
  const before = template.slice(0, start)
  const after = template.slice(end)
  const lead = before && !/\s$/.test(before) ? ' ' : ''
  const trail = after && !/^\s/.test(after) ? ' ' : ''
  const inserted = `${lead}${token}${trail}`
  return { value: `${before}${inserted}${after}`, caret: before.length + lead.length + token.length }
}
